import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter } from '@patsnap/tahiti-core';
import { FeatureCompareModel } from '../../models/feature-compare-model';
import { GetClaimCompareResultRepository, GetClaimCompareTranslationRepository, GetClaimFeatureRepository } from '../claim-charting-repository';
import type { FeatureCompareService } from '../feature-compare-service';
import type { ClaimCompareTranslationData, ClaimFeatureData, CompareFeatureData, CompareFeatureItem, FeatureCompareState, FeatureDismantlingLocalData, GetClaimCompareTranslationParams, IdParams, LangModeInfo, PatentItemInfo  } from '../../types';
import { IndexDBService } from '../index-data-base-service';
import { ClaimGoogleTransLang, StreamStatus, TransSourcePatent, TransSourcePatentKey } from '../../types/enum';
import { ID_PREFIX, PARAMS_ID_DEFAULT } from '../../constants';
import { LangTypeMode } from '@/domains/translation/enums';
import { PatentBasicService } from '@/domains/patent/services/patent-basic-service';
import { I18nService } from '@analytics/shared/services/i18n.interface';
import { Message } from '@patsnap/tahiti-core/common/message';
import type { PsHttpSSE } from '@patsnap-utils/sse';
import type { ApiGetClaimFeatureResponse } from '@/infrastructure/api-functions/core-search-api/get-claim-feature-api';
import type { ApiGetClaimCompareResultResponse } from '@/infrastructure/api-functions/core-search-api/get-claim-compare-result-api';
import { ExportExcelService } from '../export-excel-service';
@injectable()
export class FeatureCompareServiceImpl implements FeatureCompareService {
    private _onStatesChangeEmitter = new Emitter<FeatureCompareState>();
    readonly onStatesChange = this._onStatesChangeEmitter.event;

    @inject(GetClaimFeatureRepository)
    private _getClaimFeatureRepository!: GetClaimFeatureRepository;
    @inject(GetClaimCompareTranslationRepository)
    private _getClaimCompareTranslationRepository!: GetClaimCompareTranslationRepository;
    @inject(GetClaimCompareResultRepository)
    private _getClaimCompareResultRepository!: GetClaimCompareResultRepository;
    @inject(IndexDBService)
    private _indexDBService!: IndexDBService<FeatureDismantlingLocalData>;
    @inject<PatentBasicService>(PatentBasicService)
    private _patentBasicService!: PatentBasicService;
    @inject(ExportExcelService)
    private _exportExcelService!: ExportExcelService;
    private _stream: PsHttpSSE<ApiGetClaimFeatureResponse | ApiGetClaimCompareResultResponse> | null = null;

    constructor(
        @inject(FeatureCompareModel)
        private _model: FeatureCompareModel,
        @inject(Message)
        private _message: Message,
        @inject(I18nService)
        private _i18n: I18nService,

    ) {
        this._model.onStatesChange(this._emitModelDataChange.bind(this));

    }


    private _emitModelDataChange() {
        this._onStatesChangeEmitter.fire(this.getModelData());
    }

    public get transLangInfo(): LangModeInfo {
        return {
            lang: this._model.modelData.transLang,
            langType: this._model.modelData.transLangType,
        };
    }

    private _updateComparePatentInfo(targetInfo: PatentItemInfo) {
        // StreamStatus.PROGRESS_START添加的一个流开始状态，是没获取到流返回之前，保持禁用状态的
        this.updateModelData({ descPatentSN: targetInfo.PN, descPatentId: targetInfo.PATENT_ID, compareStreamStatus: StreamStatus.PROGRESS_START });
    }

    // 选中对比数据时，如果默认带了翻译状态，需要在流结束时在调翻译接口
    public async getFeatureCompareResult(targetInfo: PatentItemInfo): Promise<void> {
        // 先把对比数据更新到state中，后面带到接口入参
        this._updateComparePatentInfo(targetInfo);
        const compareData = this.getModelData();
        this._stream = this._getClaimCompareResultRepository.getClaimCompareResult(
            compareData,
            (status: StreamStatus, content?: Partial<CompareFeatureData>) => {
                // 如果返回的content没有值就无值展示
                const data = content ?? {};
                this.updateModelData({ ...data, compareStreamStatus: status });
                // 检查翻译语言和模式是否需要调用翻译接口
                if (status === StreamStatus.FINISH) {
                    const { claimLang } = compareData;
                    const { lang, langType } = this.transLangInfo;

                    // 翻译语言不等于claim语言，且翻译模式不等于原文
                    if (claimLang !== lang && langType !== LangTypeMode.ORIGINAL) {
                        // 调翻译接口
                        this._featureCompareTranslation();
                    }
                }
            },
            (error: { error_code?: number }) => {
                const errorCode = (error as { error_code?: number }).error_code || 400;
                this._message.closeAll();
                this._message.warning({
                    message: this._i18n.t(`commonStatusCode.${errorCode}`),
                    showClose: true,
                });
            },
        );
    }

    public alertWarning(message: string, showClose: boolean) {
        this._message.closeAll();
        this._message.warning({
            message,
            showClose,
        });
    }

    public getModelData() {
        return this._model.modelData;
    }

    public updateModelData(data: Partial<FeatureCompareState>) {
        // 无外部引用可以用private
        this._model.update(() => ({
            ...this._model.modelData,
            ...data,
        }));
    }

    private _formatCompareData(data: FeatureDismantlingLocalData | ClaimFeatureData, idParams: IdParams): Partial<FeatureCompareState> {
        const { claims, claimLang, patentSN } = data;
        const transLang = ('transLang' in data ? data.transLang : ClaimGoogleTransLang.DEFAULT);
        const isIndepFeatureNames: string[] = [];
        const formattedData: Partial<FeatureCompareState> = {
            claimPatentId: idParams.targetId,
            descPatentId: idParams.sourceId,
            claimPatentSN: patentSN,
            claimLang,
            transLang,
            compares: claims?.map(claim => {
                if(claim.isIndep) isIndepFeatureNames.push(claim.name);
                const compareFeatures = claim.features.map(feature => {
                    return { id: feature.id, claimVal: feature.value, claimTrans: feature.trans };
                });
                return {
                    name: claim.name,
                    isIndep: claim.isIndep,
                    compareFeatures,
                };
            }),
            isIndepFeatureNames,
        };
        return formattedData;
    }

    private async _collectPageInfoDoCompare(idParams: IdParams) {
        const descPatent = await this._patentBasicService.getPatentBase(idParams.sourceId);
        this.getFeatureCompareResult({
            PN: descPatent.PN,
            TITLE: '',
            PATENT_ID: descPatent.PATENT_ID || idParams.sourceId,
        });
    }

    // 进入对比页面
    public async getClaimFeatureData(idParams: IdParams) {
        // 先取本地 如果没有则调接口取特征拆分
        const localData = await this.getClaimFeatureLocalData(idParams);

        if (localData?.patentId) {
            const data = this._formatCompareData(localData, idParams);
            const transLangType = data.transLang!== ClaimGoogleTransLang.DEFAULT ? LangTypeMode.TRANSLATION : LangTypeMode.ORIGINAL;
            this.updateModelData({ ...data, transLangType });
        } else {
            this._stream = this._getClaimFeatureRepository.getClaimFeatures(
                idParams.targetId,
                (streamStatus: StreamStatus, content?: ClaimFeatureData) => {
                    const data = content ? this._formatCompareData(content, idParams) : {};
                    this.updateModelData({ ...data, claimStreamStatus: streamStatus });
                    // 如果路由IdParams两个id都存在，再掉对比接口
                    if (streamStatus === StreamStatus.FINISH && idParams.sourceId !== PARAMS_ID_DEFAULT) {
                        this._collectPageInfoDoCompare(idParams);
                    }
                },
                (error: { error_code?: number }) => {
                    const errorCode = (error as { error_code?: number }).error_code || 400;
                    // 超出使用容量错误码
                    this._message.closeAll();
                    this._message.warning({
                        message: this._i18n.t(`commonStatusCode.${errorCode}`),
                        showClose: true,
                    });
                },
            );
        }
    }

    private _updateTransLoading(val: boolean) {
        this.updateModelData({ transLoading: val });
    }

    private async _getTransLogic(params: GetClaimCompareTranslationParams) {
        // 翻译的语言模式为原文，或者原文和翻译语言一致时不走接口请求
        if (params.langType === LangTypeMode.ORIGINAL || params.sourceLang === params.transLang) {
            return {};
        }
        return await this._getClaimCompareTranslationRepository.getClaimCompareTranslation(params);
    }

    private _formatTransParams(params: LangModeInfo, type: TransSourcePatent): GetClaimCompareTranslationParams {
        const data = this.getModelData();

        const extractText = (key: TransSourcePatentKey) =>
            data.compares.flatMap(el => el.compareFeatures.map(item => item[key]!));

        const createParams = (patentId: string, sourceLang: string, text: string[]): GetClaimCompareTranslationParams => ({
            patentId,
            sourceLang,
            transLang: params.lang,
            text,
            langType: params.langType,
        });

        const textClaim = extractText(TransSourcePatentKey.CLAIM);
        const textCompare = extractText(TransSourcePatentKey.COMPARE);

        // 优先使用 V2 字段，fallback 到 V1 字段
        const targetPatentId = data.targetPatentId || data.claimPatentId || '';
        const targetLang = data.claimLang || 'EN';
        const filePatentId = data.filePatentId || data.descPatentId || '';
        const fileLang = data.descLang || 'EN';

        return type === TransSourcePatent.CLAIM
            ? createParams(targetPatentId, targetLang, textClaim)
            : createParams(filePatentId, fileLang, textCompare);
    }

    private _formatTransRes(res: ClaimCompareTranslationData | {}) {
        // 切换翻译原文时，清除下面翻译内容；切换翻译当原文和翻译语言一样时，直接展示原文，不请求接口
        const optData = this.getModelData();
        optData.compares.map(claim => {
            claim.compareFeatures.map(feature => {
                feature.claimTrans = optData.transLang !== optData.claimLang ? res[feature.claimVal] : feature.claimVal;
                feature.compareTrans = optData.transLang !== optData.descLang ? res[feature.compareVal!] : feature.compareVal;
                return feature;
            });
            return claim;
        });
        this.updateModelData(optData);
    }

    private async _fetchTranslation(type: TransSourcePatent) {
        const transParams = this._formatTransParams(this.transLangInfo, type);
        const res = await this._getTransLogic(transParams);
        return res;
    }

    private async _featureCompareTranslation() {
        this._updateTransLoading(true);
        // 当没有对比patentId时，只有本地特征数据
        // 有对比数据和本地数据
        if (this._model.modelData.descPatentId === PARAMS_ID_DEFAULT) {
            const transData = await this._fetchTranslation(TransSourcePatent.CLAIM);
            this._formatTransRes(transData);
            this._updateTransLoading(false);
        } else {
            const [claimTrans, compareTrans] = await Promise.all([await this._fetchTranslation(TransSourcePatent.CLAIM), await this._fetchTranslation(TransSourcePatent.COMPARE)]);
            this._formatTransRes({ ...claimTrans, ...compareTrans });
            this._updateTransLoading(false);
        }
    }

    // 获取翻译数据
    public async getFeatureCompareTranslation(params: LangModeInfo) {
        this.updateModelData({ transLang: params.lang, transLangType: params.langType });
        this._featureCompareTranslation();
    }

    // 获取本地存储数据
    public async getClaimFeatureLocalData(idParams: IdParams) {
        const res = await this._indexDBService.getData(`${ID_PREFIX}${idParams.targetId}`);
        this._indexDBService.clearData(`${ID_PREFIX}${idParams.targetId}`);
        return res;
    }

    // 导出
    public async exportExcel(data: CompareFeatureItem[]) {
        const excelParams = {
            hasTransLang: this.transLangInfo.lang && this.transLangInfo.lang !== ClaimGoogleTransLang.DEFAULT,
            claimPN: this._model.modelData.claimPatentSN,
            descPN: this._model.modelData.descPatentSN,
        };
        this._exportExcelService.exportCompareExcel(data, excelParams);
    }

    private _closeUpstream() {
        this._stream?.close();
    }

    public reset() {
        this._closeUpstream();
        this.updateModelData(this._model.defaultModelData);
    }
}
